import { CommonModule } from "@angular/common";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { NgModule } from "@angular/core";
import { HttpModule } from "@angular/http";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import {
  MatInputModule,
  MatTabsModule,
  MatChipsModule,
  MatTableModule,
  MatCheckboxModule,
  MatProgressSpinnerModule,
  MatSortModule,
  MatButtonModule,
  MatRadioModule,
  MatDatepickerModule,
  MatNativeDateModule,
  MatPaginatorModule,
  MatTooltipModule,
  MatListModule,
  MatCardModule,
} from "@angular/material";
import { MatSelectModule } from "@angular/material/select";
import { HomeComponent } from "./home.component";
import { HomeRoutingModule } from "./home-routing.module";
import { HomeAuthGuard } from "./home-auth-guard.service";
import { HeaderComponent } from "./header/header.component";
import { PopoverModule } from "ngx-popover";
import { ModalComponent } from "../components/modal/modal.component";
import { ClickOutsideDirective } from "../directives/click-outside.directive";
import { EditDisplayNameComponent } from "./header/edit-display-name-modal.component";
import { ChangePasswordComponent } from "./header/change-password-modal.component";
import { KeysPipe } from "../pipes/keys.pipe";
import { GroupByPipe } from "../pipes/group-by.pipe";
import { DataDetailsComponent } from "./data/data-details.component";
import { DataDetailsTabComponent } from "./data/tabs/details-tab.component";
import { ActivityLogComponent } from "./data/activity-log/activity-log.component";
import { DropdownComponent } from "../components/dropdown/dropdown.component";
import { SearchService } from "../services/search.service";
import { EditTagsComponent } from "./data/edit-tags/edit-tags.component";
import { SearchGuard } from "./search-guard.service";
import { UploaderComponent } from "./uploader/uploader.component";
import { HttpClientModule } from "@angular/common/http";
import { MetabolomicMetaComponent } from "./uploader/modals/metabolomic-meta-modal.component";
import { SubmitUploadComponent } from "./uploader/modals/submit-modal.component";
import { GeneticMetaComponent } from "./uploader/modals/genetic-meta-modal.component";
import { ImageMetaComponent } from "./uploader/modals/image-meta-modal.component";
import { TagListComponent } from "../components/tag-list/tag-list.component";
import { FileDownloadModalComponent } from "./data/file-download-confirm-modal.component";
import { ApproveUploadComponent } from "./reports/approve-modal.component";
import { EditNotesComponent } from "./data/edit-notes/edit-notes.component";
import { LoadingSpinnerService } from "../components/loading-spinner/loading-spinner.service";
import { LoadingSpinnerComponent } from "../components/loading-spinner/loading-spinner.component";
import { GradingMetaComponent } from "./uploader/modals/grading-meta-modal.component";
import { SearchComponent } from "./search/search.component";
import { BatchDownloadModalComponent } from "../components/modal/modals/batch-download-modal.component";
import { ClearSearchComponent } from "./search/clear-search-modal.component";
import { PatientListingComponent } from "./patient-listing/patient-listing.component";
import { ManageSearchTagsComponent } from "./manage-search-tags/manage-search-tags.component";
import { EditSearchTagModalComponent } from "./manage-search-tags/edit-search-tag-modal.component";
import { DeleteConfirmationModalComponent } from "../components/modal/modals/delete-confirmation-modal.component";
import { LowyGridComponent } from "../components/grid/grid.component";
import { SearchResultsComponent } from "./search/results/search-results.component";
import { SharedModule } from "../shared.module";
import { RejectUploadComponent } from "./reports/reject-modal.component";
import { ManageUsersComponent } from "./manage-users/manage-users.component";
import { EditUserModalComponent } from "./manage-users/edit-user-modal.component";
import { ReportsComponent } from "./reports/reports.component";
import { EmailSearchResultsComponent } from "./search/email-search-results-modal.component";
import { AdjudicateComponent } from "./grading/adjudication/adjudication-modal.component";
import { MactelDiagnosisTabComponent } from "./data/tabs/mactel-diagnosis-tab.component";
import { AdjudicationActivityLogComponent } from "./data/adjudication-activity-log/adjudication-activity-log.component";
import { PackageCommentsComponent } from "./reports/package-comments-modal.component";
import { GradingComponent } from "./grading/grading/grading.component";
import { EtdrsComponent } from "./grading/etdrs-grid/etdrs-grid.component";
import { OnlyDigitsDirective } from "../directives/only-digits.directive";
import { ConfirmDatalockModalComponent } from "./reports/modals/confirm-datalock-modal.component";
import { GenericConfirmationModalComponent } from "../components/modal/modals/generic-confirmation-modal.component";
import { ReportBuilderComponent } from "./report-builder/report-builder.component";
import { DataShareService } from "../services/data-share.service";
import { ThreeDigitsDecimalMax10ValidatorDirective } from "../validators/three-digits-decimal-max-10.validator";
import { ThreeDigitsDecimalMax10orTripleDigitsValidatorDirective } from "../validators/three-digits-decimal-max-10-or-triple-digits.validator";
import { AllowedValuesValidatorDirective } from "../validators/allowed-values.validator";
import { ThreeDigitsOr789sValidatorDirective } from "../validators/three-digits-or-789s.validator";
import { CanLeaveGradingGuard } from "./leave-grading-guard.service";
import { ViewableImageComponent } from "./data/viewable-image/viewable-image.component";
import { ManageSecondaryPatientIdsComponent } from "./header/manage-secondary-ids.component";
import { AddRemoveSearchColumnsComponent } from "./search/search-modals/add-remove-columns-modal.component";
import { EditSearchTagsComponent } from "./search/search-modals/edit-search-tags-modal.component";
import { GridFilterComponent } from "../components/grid/grid-filter/grid-filter.component";
import { SelectPatientsComponent } from "./search/search-modals/select-patients-modal.component";
import { ResetFiltersModalComponent } from "./search/search-modals/reset-filters-modal.component";
import { SelectImageColumnsComponent } from "./search/search-modals/select-image-columns-modal.component";
import { SelectGeneticColumnsComponent } from "./search/search-modals/select-genetic-columns-modal.component";
import { GridPaginatorComponent } from "../components/grid/grid-paginator/grid-paginator.component";
import { BulkAddTagsModalComponent } from "./bulk-add-tags/bulk-add-tags-modal.component";
import { DataDictionaryComponent } from "./data-dictionary/data-dictionary.component";
import { ReportColumnsModalComponent } from "./report-builder/report-columns-modal.component";

@NgModule({
  imports: [
    HttpModule,
    HomeRoutingModule,
    PopoverModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    MatTabsModule,
    MatChipsModule,
    MatTableModule,
    MatCheckboxModule,
    MatSelectModule,
    MatSortModule,
    MatButtonModule,
    MatPaginatorModule,
    MatRadioModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatProgressSpinnerModule,
    BrowserAnimationsModule,
    HttpClientModule,
    MatTooltipModule,
    MatListModule,
    MatCardModule,
    SharedModule,
  ],
  declarations: [
    HomeComponent,
    HeaderComponent,
    ModalComponent,
    ClickOutsideDirective,
    ThreeDigitsDecimalMax10ValidatorDirective,
    ThreeDigitsDecimalMax10orTripleDigitsValidatorDirective,
    AllowedValuesValidatorDirective,
    ThreeDigitsOr789sValidatorDirective,
    EditDisplayNameComponent,
    ChangePasswordComponent,
    ManageSecondaryPatientIdsComponent,
    KeysPipe,
    GroupByPipe,
    DataDetailsComponent,
    DataDetailsTabComponent,
    ActivityLogComponent,
    DropdownComponent,
    EditTagsComponent,
    UploaderComponent,
    MetabolomicMetaComponent,
    SubmitUploadComponent,
    GeneticMetaComponent,
    ImageMetaComponent,
    TagListComponent,
    FileDownloadModalComponent,
    ApproveUploadComponent,
    RejectUploadComponent,
    EditNotesComponent,
    LoadingSpinnerComponent,
    GradingMetaComponent,
    LowyGridComponent,
    SearchComponent,
    SearchResultsComponent,
    BatchDownloadModalComponent,
    ClearSearchComponent,
    PatientListingComponent,
    DataDictionaryComponent,
    ManageSearchTagsComponent,
    EditSearchTagModalComponent,
    DeleteConfirmationModalComponent,
    ManageUsersComponent,
    EditUserModalComponent,
    ReportsComponent,
    EmailSearchResultsComponent,
    AdjudicateComponent,
    MactelDiagnosisTabComponent,
    AdjudicationActivityLogComponent,
    PackageCommentsComponent,
    GradingComponent,
    EtdrsComponent,
    OnlyDigitsDirective,
    ConfirmDatalockModalComponent,
    GenericConfirmationModalComponent,
    ReportBuilderComponent,
    ViewableImageComponent,
    AddRemoveSearchColumnsComponent,
    EditSearchTagsComponent,
    GridFilterComponent,
    SelectPatientsComponent,
    ResetFiltersModalComponent,
    SelectImageColumnsComponent,
    SelectGeneticColumnsComponent,
    GridPaginatorComponent,
    BulkAddTagsModalComponent,
    ReportColumnsModalComponent,
  ],
  providers: [
    HomeAuthGuard,
    SearchGuard,
    SearchService,
    LoadingSpinnerService,
    DataShareService,
    CanLeaveGradingGuard,
  ],
})
export class HomeModule {}



// WEBPACK FOOTER //
// ./src/app/home/home.module.ts